<template>
  <div class="min-h-screen bg-gray-950">
    <SiteNavBar />

    <PageContainer as="main" class="py-16 lg:py-24">
      <Breadcrumbs :items="breadcrumbs" class="mb-8" @navigate="trackBreadcrumb" />

      <BaseHeading as="h1" size="display" class="max-w-4xl">
        The colorize.design
        <GradientText>blog</GradientText>
      </BaseHeading>
      <p class="mt-6 max-w-2xl text-lg leading-8 text-gray-300">
        Guides to color palettes, brand identity and design systems.
      </p>

      <!-- Category filtering happens in the browser rather than on routes of
           its own. A section page with two posts on it is thin content that
           competes with the articles themselves for the same terms; filtering
           here keeps one strong indexable listing and still lets a reader
           narrow it down. The URL is left alone for the same reason. -->
      <div
        v-if="categories.length"
        class="mt-10 -mx-1 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <SegmentedControl
          :options="categoryOptions"
          :model-value="activeCategory"
          label="Filter articles by category"
          @select="selectCategory"
        />
      </div>

      <ul v-if="visiblePosts.length" role="list" class="mt-12 grid grid-cols-1 gap-x-10 gap-y-14 sm:grid-cols-2">
        <PostCard
          v-for="(post, index) in visiblePosts"
          :key="post.slug"
          :post="post"
          :featured="index === 0 && !activeCategory && post.featured"
          :eager="index === 0"
        />
      </ul>

      <p v-else class="mt-12 text-gray-400">
        Nothing in {{ activeCategoryName }} yet.
        <TextLink tone="accent" @click="selectCategory('')">Show all articles</TextLink>
      </p>

      <!-- Same API panel the generator and the brand hub close with. -->
      <div class="mt-16">
        <ApiAccessCard heading-tag="h2" />
      </div>
    </PageContainer>

    <SiteFooter />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { trackEvent } from '~/composables/analytics'
import BaseHeading from '~/components/atoms/BaseHeading.vue'
import GradientText from '~/components/atoms/GradientText.vue'
import PageContainer from '~/components/atoms/PageContainer.vue'
import TextLink from '~/components/atoms/TextLink.vue'
import Breadcrumbs from '~/components/molecules/Breadcrumbs.vue'
import SegmentedControl from '~/components/molecules/SegmentedControl.vue'
import SiteNavBar from '~/components/organisms/SiteNavBar.vue'
import SiteFooter from '~/components/organisms/SiteFooter.vue'
import PostCard from '~/components/molecules/PostCard.vue'
import ApiAccessCard from '~/components/molecules/ApiAccessCard.vue'
import { SITE_URL } from '~/utils/site'

// Resolved at prerender time (see nitro.prerender.routes), so the listing ships
// inside the static HTML — no client-side request, nothing for a crawler to
// wait on. `default` keeps the template safe if the payload is ever missing.
const { data } = await useAsyncData('blog-index', () => $fetch('/api/blog'), {
  default: () => ({ posts: [], categories: [] }),
})

const posts = computed(() => data.value?.posts ?? [])
// Only the sections that have posts in them, in the order the build defines.
const categories = computed(() => data.value?.categories ?? [])

const activeCategory = ref('')

const categoryOptions = computed(() => [
  { value: '', label: 'All articles' },
  ...categories.value.map((category) => ({ value: category.slug, label: category.name })),
])

const breadcrumbs = [{ label: 'Colorize', to: '/' }, { label: 'Blog' }]

const visiblePosts = computed(() => (
  activeCategory.value
    ? posts.value.filter((post) => post.category === activeCategory.value)
    : posts.value
))

const activeCategoryName = computed(() => (
  categories.value.find((category) => category.slug === activeCategory.value)?.name || 'this section'
))

const selectCategory = (slug) => {
  activeCategory.value = activeCategory.value === slug ? '' : slug
  try {
    trackEvent('Blog Category Filtered', { category: activeCategory.value || 'all' })
  } catch {}
}

const trackBreadcrumb = () => {
  try {
    trackEvent('Blog Breadcrumb (generator)')
  } catch {}
}

// --- SEO --------------------------------------------------------------------

const canonical = `${SITE_URL}/blog`
const feedUrl = `${SITE_URL}/blog/rss.xml`
const title = 'Blog - Color Palettes, Brand Colors & Design Guides | Colorize'
const description =
  'Practical guides on building color palettes, extracting brand colors and running a design system, from the team behind the Colorize palette generator.'

// The listing is resolved before this runs (the useAsyncData above is awaited),
// so the JSON-LD is a plain string baked into the prerendered document.
//
// Blog + ItemList rather than a bare CollectionPage: it tells a crawler this is
// a periodical with dated entries, which is what makes the individual
// BlogPosting nodes on each article page make sense as a set.
const jsonLd = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${canonical}#blog`,
  url: canonical,
  name: 'Colorize Blog',
  description,
  inLanguage: 'en',
  isPartOf: { '@type': 'WebSite', name: 'Colorize', url: SITE_URL },
  publisher: {
    '@type': 'Organization',
    name: 'Colorize',
    url: SITE_URL,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/web-app-manifest-512x512.png` },
  },
  blogPost: posts.value.map((post) => ({
    '@type': 'BlogPosting',
    '@id': `${SITE_URL}${post.path}#article`,
    url: `${SITE_URL}${post.path}`,
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    ...(post.updated ? { dateModified: post.updated } : {}),
    author: { '@type': 'Person', name: post.author },
    ...(post.image ? { image: `${SITE_URL}${post.image}` } : {}),
  })),
  // `<` can't appear raw inside a <script> block without ending it early.
}).replace(/</g, '\\u003c')

useHead({
  title,
  link: [
    { rel: 'canonical', href: canonical },
    // Feed discovery: this is the tag readers and aggregators look for.
    { rel: 'alternate', type: 'application/rss+xml', title: 'Colorize Blog', href: feedUrl },
  ],
  htmlAttrs: { lang: 'en' },
  script: [{ type: 'application/ld+json', innerHTML: jsonLd }],
})

useSeoMeta({
  description,
  ogTitle: title,
  ogDescription: description,
  ogType: 'website',
  ogUrl: canonical,
  ogImage: `${SITE_URL}/og-image.jpg`,
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: `${SITE_URL}/og-image.jpg`,
})
</script>
